/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-array-index-key */
/* eslint-disable consistent-return */
/* eslint-disable react/prop-types */
import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import DayView from './DayView';
import WeekView from './WeekView';
import MonthView from './MonthView';
import ShimmerContent from './ShimmerContent';
import OnboardingModal from '../../../../shared/_TBComponents/OnboardingModal';
import orderingInfoContent from './OrderingInfoModalContent';
import {
  getAssignedWorkoutItemsForCalendarByDate,
  getAssignedWorkoutItemsForAthleteByDate,
  getRecurringWorkoutsByDate,
  fetchNonDateBasedProgramItemsByDayRange,
  setSelectedReduxCalendar,
  setSelectedReduxAthlete,
  clearBeforeRecurringCalendarItems,
  clearAfterRecurringCalendarItems,
  setSelectedDate,
  clearCalendarItems,
  setSelectedProgramDayNumber,
  setIsPrimaryAnimatedSideBarOpen,
  setOrderingInfoModalOpen,
} from '../../ducks/calendarActions';
import {
  isApproachingEdge,
  calculate69DayPaddedRange,
  isApproachingNewRecurringRange,
} from '../../utils/weekViewFetchRange';

const Container = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: visible;
`;

const ExerciseContainer = styled('div')`
  display: flex;
  flex-direction: column;
  position: relative;
  height: 100%;
`;

const shimmerCount = [1, 2, 3, 4, 5, 6, 7, 8, 9];

const CalendarPanel = ({ scrollContainerRef }) => {
  const dispatch = useDispatch();
  const onBeforeCapture = useSelector((state) => state.calendar.data.onBeforeCapture);
  const selectedReduxCalendar = useSelector((state) => state.calendar.data.selectedReduxCalendar);
  const selectedReduxNonDateBasedProgram = useSelector(
    (state) => state.calendar.data.selectedReduxNonDateBasedProgram,
  );
  const selectedReduxAthlete = useSelector((state) => state.calendar.data.selectedReduxAthlete);
  const isCalendarItemsLoading = useSelector((state) => state.calendar.ui.isCalendarItemsLoading);
  const isSavedItemsLoading = useSelector((state) => state.calendar.ui.isSavedItemsLoading);
  const viewType = useSelector((state) => state.calendar.data.viewType);
  const selectedWeekViewDayToggle = useSelector(
    (state) => state.calendar.data.selectedWeekViewDayToggle,
  );
  const programType = useSelector((state) => state.calendar.data.programType);
  const selectedProgramDayNumber = useSelector(
    (state) => state.calendar.data.selectedProgramDayNumber,
  );
  const isOrderingInfoModalOpen = useSelector(
    (state) => state.calendar.data.isOrderingInfoModalOpen,
  );

  /** Data fetching related selectors */
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const calendarItemsDateRange = useSelector((state) => state.calendar.data.calendarItemsDateRange);
  const recurringItemsDateRange = useSelector(
    (state) => state.calendar.data.recurringItemsDateRange,
  );
  const monthViewWindowStart = useSelector(
    (state) => state.calendar.data.monthViewWindowStart,
  );

  useEffect(() => {
    const hasProgram = Object.keys(selectedReduxNonDateBasedProgram || {}).length > 0;
    if (programType === 'free-form-program' && hasProgram) {
      dispatch(setSelectedReduxAthlete({}));
      dispatch(setSelectedReduxCalendar({}));
      dispatch(clearCalendarItems());
      dispatch(clearBeforeRecurringCalendarItems());
      dispatch(clearAfterRecurringCalendarItems());
      dispatch(setSelectedDate(new Date()));
    }
  }, [
    programType,
    Object.keys(selectedReduxNonDateBasedProgram).length,
    Object.keys(selectedReduxAthlete).length,
    Object.keys(selectedReduxCalendar).length,
  ]);

  useEffect(() => {
    /** need to close side bar if change from calendar to non date based
     * and visa versa, this prevents the user from getting access to a side bar
     * they should not have access to, for expample non date based
     * should not have access to calendar but if the side bar is open
     * when the user changes program types they would have access to the calendar
     * closing the side bar prevent this issue
     */
    dispatch(setIsPrimaryAnimatedSideBarOpen(false));
  }, [
    programType,
  ]);

  const prevCalendarId = useRef();
  const prevAthleteId = useRef();
  const prevUserId = useRef();

  /** Data fetching logic moved from CalendarLayout */
  useEffect(() => {
    if (!Object.keys(currentUser).length) return;
    // Month view fetches off its rolling window in a dedicated effect below.
    if (viewType === 'monthView') return;

    const calendarId = selectedReduxCalendar?.id;
    const athleteId = selectedReduxAthlete?.id;
    const userId = currentUser?.id;

    const calendarChanged = prevCalendarId.current !== calendarId;
    const athleteChanged = prevAthleteId.current !== athleteId;
    const userChanged = prevUserId.current !== userId;

    const isWeekdayToggleActive = viewType === 'weekView' && selectedWeekViewDayToggle !== 'Everyday';

    // Check if we need to fetch for new dates based on smart caching logic
    const shouldFetch = isApproachingEdge(
      selectedDate,
      calendarItemsDateRange,
      viewType,
      7,
      isWeekdayToggleActive,
    );

    // Only return if NOTHING important changed and not near edge
    if (!calendarChanged && !athleteChanged && !userChanged && !shouldFetch) {
      return;
    }

    // Use helper to calculate the padded date range to fetch
    const { firstDayMonth, lastDayMonth } = calculate69DayPaddedRange(
      selectedDate,
      isWeekdayToggleActive,
    );

    /** Fetch main calendar items */
    if (Object.keys(selectedReduxCalendar).length && Object.keys(currentUser).length) {
      if (selectedReduxCalendar?.id) {
        dispatch(getAssignedWorkoutItemsForCalendarByDate(
          currentUser,
          firstDayMonth,
          lastDayMonth,
          selectedReduxCalendar.id,
        ));
      }
    } else if (Object.keys(selectedReduxAthlete).length && Object.keys(currentUser).length) {
      dispatch(getAssignedWorkoutItemsForAthleteByDate(
        currentUser,
        firstDayMonth,
        lastDayMonth,
        selectedReduxAthlete.id,
      ));
    }

    // update refs AFTER logic
    prevCalendarId.current = calendarId;
    prevAthleteId.current = athleteId;
    prevUserId.current = userId;
  }, [
    selectedReduxCalendar,
    selectedReduxAthlete,
    currentUser,
    selectedDate,
    viewType,
    selectedWeekViewDayToggle,
  ]);

  /** Seperate useEffect to fetch data for non date based programs when in progam library view */
  useEffect(() => {
    const startDay = null;
    const endDay = null;
    /** Fetch non date based items */
    if (Object.keys(selectedReduxNonDateBasedProgram).length && Object.keys(currentUser).length) {
      if (selectedReduxNonDateBasedProgram?.id) {
        dispatch(fetchNonDateBasedProgramItemsByDayRange(
          currentUser.accountCode,
          selectedReduxNonDateBasedProgram.id,
          startDay,
          endDay,
        ));
      }
    }
  }, [
    selectedReduxNonDateBasedProgram,
    currentUser,
    selectedProgramDayNumber,
  ]);

  useEffect(() => {
    /** when we change the selected program we always need to reset
     * the selectedProgramDayNumber to 0
     */
    dispatch(setSelectedProgramDayNumber(0));
  }, [
    selectedReduxNonDateBasedProgram,
  ]);

  /** Separate effect for recurring workouts that only runs when needed
  and only in calendar based view */
  const shouldFetchRecurring = useSelector(
    (state) => state.calendar.data.shouldFetchRecurring,
  );

  const lastFetchRef = useRef({
    calendarId: null,
    athleteId: null,
    selectedDate: null,
    shouldFetchRecurring: null,
  });

  useEffect(() => {
    if (!Object.keys(currentUser).length || programType === 'free-form-program') return;
    // Month view fetches recurring items off its rolling window below.
    if (viewType === 'monthView') return;

    /** Don't fetch if the fetched calendar items call hasn't finished yet */
    /** this is not a boolean it's a time stamp - so if there is no time stamp
     * that mean that the get calendar items hasn't been called even once yet
     * which would be something we need
     */
    if (!shouldFetchRecurring) return;

    const calendarId = selectedReduxCalendar?.id || null;
    const athleteId = selectedReduxAthlete?.id || null;

    const shouldFetchRecurringTimeStampChanged = lastFetchRef.current.shouldFetchRecurring
    !== shouldFetchRecurring;
    /** Check if we actually need to fetch */
    const needsFetch = lastFetchRef.current.calendarId !== calendarId
    || lastFetchRef.current.athleteId !== athleteId
    || lastFetchRef.current.selectedDate !== selectedDate?.toString()
    || lastFetchRef.current.shouldFetchRecurring !== shouldFetchRecurring;

    if (!needsFetch) return; /** exit early if nothing changed */

    const isWeekdayToggleActive = viewType === 'weekView' && selectedWeekViewDayToggle !== 'Everyday';

    const { firstDayMonth, lastDayMonth } = calculate69DayPaddedRange(
      selectedDate,
      isWeekdayToggleActive,
    );

    const shouldFetchNewRecurringDateRange = isApproachingNewRecurringRange(
      selectedDate,
      recurringItemsDateRange,
      isWeekdayToggleActive,
    );

    if (!shouldFetchNewRecurringDateRange && !shouldFetchRecurringTimeStampChanged) {
      /** Data exists and we're not near the edge, skip fetch and shouldFetchRecurring
       * has not been updated to trigger another fetch
       */
      return;
    }

    lastFetchRef.current = {
      calendarId,
      athleteId,
      selectedDate: selectedDate?.toString(),
      shouldFetchRecurring,
    };

    if (calendarId) {
      dispatch(getRecurringWorkoutsByDate(currentUser, firstDayMonth, lastDayMonth, calendarId));
    } else if (athleteId) {
      dispatch(getRecurringWorkoutsByDate(
        currentUser,
        firstDayMonth,
        lastDayMonth,
        athleteId,
        true,
      ));
    }
  }, [
    selectedReduxCalendar,
    selectedReduxAthlete,
    currentUser,
    selectedDate,
    shouldFetchRecurring,
    recurringItemsDateRange,
    programType,
    viewType,
    selectedWeekViewDayToggle,
  ]);

  /** Month view: fetch calendar + recurring items for the rolling 8-week
   * window plus a 1-week buffer each side so minor scrolls don't refetch.
   * The server caps the programming range at 70 days (6048000000ms); the
   * 56-day window + 2x7-day buffer = 69 days, which stays under the limit.
   * calendarItems are merged (never evicted) so scrolling back to visited
   * weeks is instant; we only need each new window covered. A ref guard skips
   * refetching the same window for the same calendar/athlete. */
  const monthWindowFetchRef = useRef(null);
  useEffect(() => {
    if (viewType !== 'monthView' || programType === 'free-form-program') return;
    if (!Object.keys(currentUser).length || !monthViewWindowStart) return;

    const calendarId = selectedReduxCalendar?.id || null;
    const athleteId = selectedReduxAthlete?.id || null;
    if (!calendarId && !athleteId) return;

    const start = moment(monthViewWindowStart, 'YYYY-MM-DD')
      .subtract(7, 'days')
      .format('YYYY-MM-DD');
    const end = moment(monthViewWindowStart, 'YYYY-MM-DD')
      .add(55 + 7, 'days')
      .format('YYYY-MM-DD');

    const fetchKey = `${calendarId || `athlete:${athleteId}`}:${start}:${end}`;
    if (monthWindowFetchRef.current === fetchKey) return;
    monthWindowFetchRef.current = fetchKey;

    if (calendarId) {
      dispatch(getAssignedWorkoutItemsForCalendarByDate(currentUser, start, end, calendarId));
      dispatch(getRecurringWorkoutsByDate(currentUser, start, end, calendarId));
    } else {
      dispatch(getAssignedWorkoutItemsForAthleteByDate(currentUser, start, end, athleteId));
      dispatch(getRecurringWorkoutsByDate(currentUser, start, end, athleteId, true));
    }
  }, [
    monthViewWindowStart,
    viewType,
    selectedReduxCalendar,
    selectedReduxAthlete,
    currentUser,
    programType,
  ]);

  return (
    <Container setHeightTo100={onBeforeCapture}>
      <ExerciseContainer
        setHeightTo100={onBeforeCapture}
        viewType={viewType}
      >
        {viewType === 'dayView' ? (
          (programType !== 'free-form-program' && !isCalendarItemsLoading) || (programType === 'free-form-program' && !isSavedItemsLoading) ? (
            <DayView scrollContainerRef={scrollContainerRef} />
          )
            : shimmerCount.map((_, i) => <ShimmerContent key={i} />)
        ) : viewType === 'weekView' ? (
          <WeekView
            isWeekDayItemsLoading={(programType !== 'free-form-program' && isCalendarItemsLoading) || (programType === 'free-form-program' && (isSavedItemsLoading))}
          />
        ) : (
          <MonthView
            isMonthItemsLoading={(programType !== 'free-form-program' && isCalendarItemsLoading) || (programType === 'free-form-program' && (isSavedItemsLoading))}
          />
        )}
      </ExerciseContainer>
      {isOrderingInfoModalOpen && (
        <OnboardingModal
          isActive={isOrderingInfoModalOpen}
          isOpen={isOrderingInfoModalOpen}
          content={orderingInfoContent}
          maxHeight={null}
          minHeight={null}
          onClose={() => dispatch(setOrderingInfoModalOpen(false))}
          onPrimaryAction={() => dispatch(setOrderingInfoModalOpen(false))}
          onSecondaryAction={() => {}}
          primaryButtonText='CLOSE'
          showSingleButton
          showBadge={false}
          contentTitleSize='20px'
          contentTitleAlignText='center'
        />
      )}
    </Container>
  );
};

CalendarPanel.propTypes = { };
export default CalendarPanel;
